import {
  CreateResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  GetListParams,
  GetListResult,
  UpdateResult,
  UpdateParams,
} from "react-admin";
import { schemas, serverAPI } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { z } from "zod";
import { DateWithoutTime } from "../model/DateWithoutTime";

export interface Branch {
  id: string;
  createdAt: string | null;
  createdById: string;
  updatedAt: string | null;
  updatedById: string;
  drivingSchoolId: string;
  name: string;
  location?: string | null;
  /** "yyyy-MM-dd" */
  startDate: string;
  /** "yyyy-MM-dd" */
  endDate?: string | null;
  active: boolean;
  isDefaultBranch: boolean;
  maxStudentsForTheory: number;
  address: {
    street: string;
    postalCode: string;
    city: string;
    company: string;
    country: string;
  };
  invoiceAddress?: {
    street: string;
    postalCode: string;
    city: string;
    company: string;
    country: string;
  } | null;
  contractAddress?: {
    street: string;
    postalCode: string;
    city: string;
    company: string;
    country: string;
  } | null;
  isActive: boolean;
  publicHolidays: {
    date: number;
    name: string;
  }[];
  defaultDrivingSchoolManagerId?: string | null;
}

type BranchDto = z.infer<typeof schemas.BranchDto>;

class BranchesProvider extends AbstractDataProvider<Branch> {
  async getOne(_: string, { id }: GetOneParams<Branch>): Promise<GetOneResult<Branch>> {
    const dto = await serverAPI.getBranch({ id });
    return { data: _dtoToRecord(dto) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<Branch>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const dtos = await serverAPI.getBranches({ drivingSchoolId });
    return applyPagination(
      applySort(
        applyFilter(dtos.map(_dtoToRecord), { ...clientSideFilter, isActive: clientSideFilter.isActive ?? true }),
        sort,
      ),
      pagination,
    );
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Branch>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("branches", { filter: { drivingSchoolId: id, ...filter }, sort, pagination });
  }

  async create(
    _resource: string,
    params: { data: Omit<Branch, "id" | "createdAt" | "createdById" | "updatedAt" | "updatedBy"> },
  ): Promise<CreateResult<Branch>> {
    const {
      drivingSchoolId,
      name,
      address,
      active,
      isDefaultBranch,
      maxStudentsForTheory,
      defaultDrivingSchoolManagerId,
    } = params.data;

    const payload = {
      drivingSchoolId,
      name,
      address,
      active,
      isDefaultBranch,
      maxStudentsForTheory,
      defaultDrivingSchoolManagerId: defaultDrivingSchoolManagerId ?? null,
    };
    const dto = await serverAPI.createBranch({ payload });
    return { data: _dtoToRecord(dto) };
  }

  async update(_resource: string, params: UpdateParams<Branch>): Promise<UpdateResult<Branch>> {
    const { id, data } = params;
    const {
      name,
      address,
      invoiceAddress,
      contractAddress,
      active,
      isDefaultBranch,
      maxStudentsForTheory,
      defaultDrivingSchoolManagerId,
    } = data;
    const hasContractAddress =
      contractAddress?.city || contractAddress?.street || contractAddress?.postalCode || contractAddress?.company;
    const hasInvoiceAddress =
      invoiceAddress?.city || invoiceAddress?.street || invoiceAddress?.postalCode || invoiceAddress?.company;
    const payload = {
      name,
      address,
      invoiceAddress: hasInvoiceAddress ? invoiceAddress : null,
      contractAddress: hasContractAddress ? contractAddress : null,
      active,
      isDefaultBranch,
      maxStudentsForTheory,
      defaultDrivingSchoolManagerId: defaultDrivingSchoolManagerId ?? null,
    };
    const dto = await serverAPI.updateBranch({ id, payload });
    return { data: _dtoToRecord(dto) };
  }
}

function _dtoToRecord(dto: BranchDto): Branch {
  const { startDate, endDate, ...rest } = dto;
  const startDate_ = DateWithoutTime.fromString(startDate);
  const endDate_ = endDate && DateWithoutTime.fromString(endDate);
  const today = DateWithoutTime.today().toInt();
  return {
    ...rest,
    startDate,
    endDate,
    isActive: startDate_.toInt() <= today && (!endDate_ || endDate_.toInt() >= today),
    publicHolidays: rest.publicHolidays ?? [],
  };
}

export const branchesProvider = new BranchesProvider();
